import { useCallback, useEffect, useRef, useState } from "react";
import { isFulfilled } from "@reduxjs/toolkit";

import useAppDispatch from "hooks/useAppDispatch";
import {
  getTagStudent,
  tagStudent as tagStudentApi,
  removeStudentTag,
} from "services/pictureServices";
import { updatePictureTagStatus } from "features/picture/pictureSlice";
import { updatePicturePublishStatus } from "features/picture/pictureAction";
import { setErrorStatus } from "features/error/errorSlice";
import { PicturePublishStatus, PictureType } from "config/constants";
import type { Picture } from "types/Pictures";
import type { Student } from "types/Student";
import type { TagStudent } from "types/TagStudent";

const normalizeTag = (tag: TagStudent): TagStudent => {
  const parsedId =
    typeof tag.id === "string" ? Number.parseInt(tag.id, 10) : tag.id;
  const parsedStudentId =
    typeof tag.student_id === "string"
      ? Number.parseInt(tag.student_id, 10)
      : tag.student_id;

  return {
    ...tag,
    id: Number.isNaN(parsedId) ? tag.id : parsedId,
    student_id: Number.isNaN(parsedStudentId) ? tag.student_id : parsedStudentId,
  };
};

const mapStudentToTagStudent = (student?: Student) => {
  if (!student) return undefined;
  return {
    id: student.id,
    first_name: student.first_name,
    last_name: student.last_name,
    first_name_kata: student.first_name_kata,
    last_name_kata: student.last_name_kata,
    profile_picture_for_admin: student.profile_picture_for_admin || "",
  };
};

/**
 * Owns the tagged-student roster for a picture: loads it, and performs the
 * add/remove API calls the on-photo popover and markers trigger.
 */
const useTagStudents = (
  picture: Picture,
  students: Student[],
  isTaggingEnabled: boolean,
  onRefreshPictures?: () => void,
) => {
  const dispatch = useAppDispatch();
  const [taggedStudents, setTaggedStudents] = useState<TagStudent[]>([]);
  const [isLoadingTags, setIsLoadingTags] = useState(false);
  const [addingStudentId, setAddingStudentId] = useState<number | null>(null);
  const [removingTagId, setRemovingTagId] = useState<number | null>(null);
  const taggedStudentsRef = useRef<TagStudent[]>([]);

  useEffect(() => {
    taggedStudentsRef.current = taggedStudents;
  }, [taggedStudents]);

  const loadTaggedStudents = useCallback(async () => {
    setIsLoadingTags(true);
    setTaggedStudents([]);
    try {
      const response = await getTagStudent(picture.id);
      setTaggedStudents(response.map(normalizeTag));
    } catch (error) {
      dispatch(setErrorStatus(""));
    } finally {
      setIsLoadingTags(false);
    }
  }, [dispatch, picture.id]);

  useEffect(() => {
    loadTaggedStudents();
    // Re-fetch when tagging flips on too (e.g. a class photo just got
    // converted to individual): the picture's id doesn't change, so this
    // effect wouldn't otherwise re-run, but re-pulling here means the
    // popover never opens on a tag list that predates the conversion.
  }, [loadTaggedStudents, isTaggingEnabled]);

  const addTag = useCallback(
    async (studentId: number, coords: { x: number; y: number }) => {
      setAddingStudentId(studentId);
      try {
        const student = students.find((entry) => entry.id === studentId);
        const newTag = await tagStudentApi({
          picture_id: picture.id,
          student_id: studentId,
          axis_x: Math.max(0, Math.round(coords.x)),
          axis_y: Math.max(0, Math.round(coords.y)),
          rate: 100,
        });

        const enrichedTag = normalizeTag({
          ...newTag,
          student: mapStudentToTagStudent(student) ?? newTag.student,
        });

        setTaggedStudents((prev) => [...prev, enrichedTag]);
        dispatch(updatePictureTagStatus({ id: picture.id, isTagged: true }));
        onRefreshPictures?.();
        return true;
      } catch (error) {
        dispatch(setErrorStatus(""));
        return false;
      } finally {
        setAddingStudentId(null);
      }
    },
    [dispatch, onRefreshPictures, picture.id, students],
  );

  const removeTag = useCallback(
    async (tagId: number) => {
      setRemovingTagId(tagId);
      try {
        await removeStudentTag(tagId);

        const updated = taggedStudentsRef.current.filter(
          (entry) => String(entry.id) !== String(tagId),
        );
        setTaggedStudents(updated);

        dispatch(
          updatePictureTagStatus({
            id: picture.id,
            isTagged: updated.length > 0,
          }),
        );

        if (
          updated.length === 0 &&
          (picture.picture_type === PictureType.ai ||
            picture.picture_type === PictureType.non_ai) &&
          picture.public_status !== PicturePublishStatus.not_good &&
          picture.public_status < PicturePublishStatus.published
        ) {
          const updateAction = await dispatch(
            updatePicturePublishStatus({
              id: picture.id,
              formData: { public_status: PicturePublishStatus.not_good },
            }),
          );
          if (!isFulfilled(updateAction)) {
            dispatch(setErrorStatus(""));
          }
        }

        onRefreshPictures?.();
      } catch (error) {
        dispatch(setErrorStatus(""));
      } finally {
        setRemovingTagId(null);
      }
    },
    [dispatch, onRefreshPictures, picture.id, picture.picture_type, picture.public_status],
  );

  return {
    taggedStudents,
    isLoadingTags,
    addingStudentId,
    removingTagId,
    addTag,
    removeTag,
  };
};

export default useTagStudents;
